import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  FileDown,
  Eye,
  PackageCheck,
  Plus,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, fmtIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const OPS_NEXT = {
  "Approved": ["In Production"],
  "In Production": ["Partially Shipped", "Shipped"],
  "Partially Shipped": ["Shipped"],
  "Shipped": ["Partially Received", "Fully Received"],
  "Partially Received": ["Fully Received"],
  "Fully Received": ["Closed"],
};

export default function PurchaseOrders() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [detail, setDetail] = useState(null);
  const [rejectPo, setRejectPo] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [receivePo, setReceivePo] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const write = can("warehouse_admin", "purchasing");
  const approver = can("warehouse_admin");

  const load = () => api.get("/purchase-orders", { params: { status: statusF === "all" ? undefined : statusF } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);
  useEffect(() => { api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setWarehouses(r.data)).catch(() => setWarehouses([])); }, []);

  const act = async (id, action) => {
    try { await api.post(`/purchase-orders/${id}/${action}`); toast.success("Berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const transition = async (id, to) => {
    try { await api.post(`/purchase-orders/${id}/transition`, { to_status: to }); toast.success("Status diperbarui"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("Alasan reject wajib diisi");
    try { await api.post(`/purchase-orders/${rejectPo.id}/reject`, { reason: rejectReason }); toast.success("PO direject & dikembalikan ke Draft"); setRejectPo(null); setRejectReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportDetail = async (po) => {
    try {
      const r = await api.get(`/purchase-orders/${po.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `PO-${po.number}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export detail PO berhasil");
    } catch (e) { toast.error("Export gagal"); }
  };
  const loadReceiptAudit = async () => {
    setAuditLoading(true);
    try {
      const response = await api.get("/purchase-orders/receipt-audit");
      setAuditRows(response.data.conflicts || []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setAuditLoading(false);
    }
  };
  const openReceiptAudit = () => {
    setAuditOpen(true);
    loadReceiptAudit();
  };
  const reopenForReceipt = async (purchaseOrder) => {
    try {
      await api.post(`/purchase-orders/${purchaseOrder.id}/reopen-for-receipt`);
      toast.success(`${purchaseOrder.number} dikembalikan ke Approved`);
      await loadReceiptAudit();
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="purchase-orders-page">
      <PageHeader title="Purchase Order" subtitle="Pesanan pembelian ke supplier / konveksi">
        {user?.role === "owner" && (
          <Button
            variant="outline"
            onClick={openReceiptAudit}
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            data-testid="po-receipt-audit-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1.5" />
            Audit Penerimaan
          </Button>
        )}
        <Button variant="outline" onClick={() => setExportOpen(true)} className="border-status-passed text-status-passed hover:bg-status-passed/10" data-testid="po-export-btn"><FileDown className="w-4 h-4 mr-1.5" /> Export</Button>
        {write && <Button onClick={() => navigate("/purchase-orders/create")} data-testid="add-po-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Purchase Order</Button>}
      </PageHeader>

      <div className="mb-4">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-56 bg-white" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "Draft", "Waiting Approval", "Approved", "Partially Received", "Fully Received", "Closed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Status" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>No. PO / Referensi</TableHead><TableHead>Tanggal</TableHead><TableHead>Supplier</TableHead>
              <TableHead className="text-center">Item</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((po) => (
              <TableRow key={po.id} data-testid={`po-row-${po.id}`}>
                <TableCell>
                  <button
                    className="font-mono text-sm font-semibold text-cocoa hover:underline"
                    data-testid={`po-detail-link-${po.id}`}
                    onClick={() => setDetail(po)}
                    type="button"
                  >
                    {po.number}
                  </button>
                  {po.source === "production" && (
                    <button
                      className="ml-1.5 inline-flex items-center bg-cocoa/10 px-1.5 py-0.5 text-[10px] text-cocoa hover:bg-cocoa/20"
                      data-testid={`po-production-reference-${po.id}`}
                      onClick={() => navigate(`/production/orders?detail=${po.production_order_id}`)}
                      type="button"
                    >
                      Produksi · {po.production_order_number || "Tidak ditemukan"}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-sm">{fmtDate(po.po_date)}</TableCell>
                <TableCell>{po.supplier_name || "-"}</TableCell>
                <TableCell className="text-center">{po.items?.length || 0}</TableCell>
                <TableCell className="text-right font-semibold text-cocoa">{fmtIDR(po.total)}</TableCell>
                <TableCell><StatusBadge status={po.status} /></TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(po)} data-testid={`po-view-${po.id}`}><Eye className="w-4 h-4 text-cocoa" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => exportDetail(po)} data-testid={`po-export-detail-${po.id}`}><FileDown className="w-4 h-4 text-status-passed" /></Button>
                  {po.status === "Draft" && write && <><Button size="sm" variant="outline" onClick={() => navigate(`/purchase-orders/${po.id}/edit`)} className="border-taupe text-cocoa h-7 px-2" data-testid={`po-edit-${po.id}`}>Edit</Button><Button size="sm" onClick={() => act(po.id, "submit")} className="bg-cocoa text-ivory h-7 px-2" data-testid={`po-submit-${po.id}`}>Submit</Button></>}
                  {po.status === "Waiting Approval" && approver && <><Button size="sm" onClick={() => act(po.id, "approve")} className="bg-status-passed text-white h-7 px-2" data-testid={`po-approve-${po.id}`}>Approve</Button><Button size="sm" variant="outline" onClick={() => setRejectPo(po)} className="border-status-reject text-status-reject h-7 px-2" data-testid={`po-reject-${po.id}`}>Reject</Button></>}
                  {write && po.source !== "production" && ["Approved", "In Production", "Partially Shipped", "Shipped", "Partially Received"].includes(po.status) && <Button size="sm" onClick={() => setReceivePo(po)} className="bg-status-passed text-white h-7 px-2" data-testid={`po-receive-${po.id}`}><PackageCheck className="w-3.5 h-3.5 mr-1" />Terima</Button>}
                  {["Draft", "Waiting Approval", "Approved"].includes(po.status) && write && <Button size="sm" variant="ghost" onClick={() => act(po.id, "cancel")} className="text-status-reject h-7 px-2" data-testid={`po-cancel-${po.id}`}>Batal</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10"><ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada Purchase Order.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </Panel>

      {detail && <PODetail po={detail} onClose={() => setDetail(null)} onTransition={transition} onExport={exportDetail} canOperate={write} />}
      {receivePo && <PurchaseReceiptDialog po={receivePo} warehouses={warehouses} onClose={() => setReceivePo(null)} onDone={() => { setReceivePo(null); load(); }} />}
      {auditOpen && (
        <ReceiptAuditDialog
          loading={auditLoading}
          onClose={() => setAuditOpen(false)}
          onRefresh={loadReceiptAudit}
          onReopen={reopenForReceipt}
          rows={auditRows}
        />
      )}
      <ExportDialog open={exportOpen} setOpen={setExportOpen} />

      {rejectPo && (
        <Dialog open onOpenChange={() => setRejectPo(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-cocoa">Reject PO {rejectPo.number}</DialogTitle></DialogHeader>
            <Label>Alasan reject *</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} data-testid="po-reject-reason" placeholder="Jelaskan alasan penolakan..." />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectPo(null)} className="border-taupe text-cocoa">Batal</Button>
              <Button onClick={doReject} data-testid="po-reject-confirm" className="bg-status-reject hover:bg-red-700 text-white">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ReceiptAuditDialog({ loading, onClose, onRefresh, onReopen, rows }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        data-testid="po-receipt-audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">
            Audit Status & Penerimaan PO
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-wmuted" data-testid="po-receipt-audit-description">
          Hanya PO berstatus Fully Received atau Closed yang masih memiliki sisa penerimaan.
        </p>
        {loading ? (
          <div className="py-10 text-center text-wmuted" data-testid="po-receipt-audit-loading">
            Memeriksa Purchase Order...
          </div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>No. PO</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Status Saat Ini</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} data-testid={`po-receipt-audit-row-${row.id}`}>
                    <TableCell className="font-mono text-xs">{row.number}</TableCell>
                    <TableCell>{row.supplier_name || "-"}</TableCell>
                    <TableCell><StatusBadge status={row.status} /></TableCell>
                    <TableCell className="text-right">{row.total_ordered}</TableCell>
                    <TableCell className="text-right">{row.total_received}</TableCell>
                    <TableCell className="text-right font-semibold text-status-reject">
                      {row.total_remaining}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.can_reopen ? (
                        <Button
                          size="sm"
                          onClick={() => onReopen(row)}
                          data-testid={`po-reopen-for-receipt-${row.id}`}
                          className="bg-amber-600 text-white hover:bg-amber-700"
                        >
                          Kembalikan ke Approved
                        </Button>
                      ) : (
                        <span
                          className="text-xs text-wmuted"
                          data-testid={`po-receipt-audit-review-${row.id}`}
                        >
                          Perlu rekonsiliasi
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-10 text-center text-wmuted" data-testid="po-receipt-audit-empty">
            Tidak ada PO dengan status dan penerimaan yang tidak sesuai.
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onRefresh}
            data-testid="po-receipt-audit-refresh"
            disabled={loading}
          >
            Audit Ulang
          </Button>
          <Button
            onClick={onClose}
            data-testid="po-receipt-audit-close"
            className="bg-cocoa text-ivory"
          >
            Tutup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseReceiptDialog({ po, warehouses, onClose, onDone }) {
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || "");
  const [rows, setRows] = useState(() => po.items.map((item) => ({ ...item, receive_now: item.remaining_qty || 0 })));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/purchase-orders/${po.id}/receive`, { warehouse_id: warehouseId, notes, items: rows.map((row) => ({ variant_id: row.variant_id, qty: parseInt(row.receive_now) || 0 })) });
      toast.success(`Penerimaan ${response.data.receipt.receipt_number} berhasil dicatat`);
      onDone();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <Dialog open onOpenChange={onClose}><DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="po-receipt-dialog"><DialogHeader><DialogTitle className="font-heading text-cocoa">Penerimaan PO — {po.number}</DialogTitle></DialogHeader><div className="space-y-4"><div><Label>Gudang Tujuan</Label><Select value={warehouseId} onValueChange={setWarehouseId}><SelectTrigger className="bg-white" data-testid="po-receipt-warehouse"><SelectValue placeholder="Pilih gudang" /></SelectTrigger><SelectContent>{warehouses.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent></Select></div><Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk / Varian</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead className="text-right">Diterima Sekarang</TableHead></TableRow></TableHeader><TableBody>{rows.map((row, index) => <TableRow key={row.variant_id}><TableCell><div className="font-medium text-cocoa">{row.product_name}</div><div className="text-xs text-wmuted">{row.color}/{row.size}</div></TableCell><TableCell className="text-right">{row.remaining_qty}</TableCell><TableCell><Input type="number" min="0" max={row.remaining_qty} value={row.receive_now} onChange={(event) => setRows(rows.map((item, i) => i === index ? { ...item, receive_now: event.target.value } : item))} className="w-24 h-8 text-right ml-auto" data-testid={`po-receipt-qty-${row.variant_id}`} /></TableCell></TableRow>)}</TableBody></Table><div><Label>Catatan penerimaan</Label><Textarea value={notes} onChange={(event) => setNotes(event.target.value)} className="mt-1" data-testid="po-receipt-notes" /></div></div><DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={busy || !warehouseId} data-testid="po-receipt-submit" className="bg-status-passed text-white">{busy ? "Menyimpan..." : "Simpan Penerimaan"}</Button></DialogFooter></DialogContent></Dialog>;
}

const REPORTS = [
  { value: "rekap-pembelian-produk", label: "Rekap Pembelian Produk", filter: "supplier" },
  { value: "rekap-transaksi", label: "Rekap Transaksi Pembelian", filter: "supplier" },
  { value: "rekap-transaksi-produk", label: "Rekap Transaksi By Produk", filter: "product" },
];

function ExportDialog({ open, setOpen }) {
  const [report, setReport] = useState("rekap-pembelian-produk");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const [supplierId, setSupplierId] = useState("");
  const [productId, setProductId] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  const filterType = (REPORTS.find((r) => r.value === report) || {}).filter;

  useEffect(() => {
    if (!open) return;
    api.get("/suppliers", { params: { active: "active" } }).then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/products", { params: { status: "active" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const start = startDate ? `${startDate}T${startTime || "00:00"}:00` : undefined;
      const end = endDate ? `${endDate}T${endTime || "23:59"}:59` : undefined;
      const params = { report, start, end };
      if (filterType === "supplier") params.supplier_id = supplierId || undefined;
      if (filterType === "product") params.product_id = productId === "all" ? undefined : productId;
      const r = await api.get("/purchase-orders/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${report}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export Excel berhasil"); setOpen(false);
    } catch (e) { toast.error("Export gagal"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl" data-testid="po-export-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Export Purchase Order</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-5" data-testid="po-export-report">
            {REPORTS.map((rp) => (
              <label key={rp.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="po-report" checked={report === rp.value} onChange={() => setReport(rp.value)} data-testid={`po-report-${rp.value}`} />
                {rp.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-white" data-testid="po-export-start-date" /></div>
            <div><Label>Start Time</Label><Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="bg-white" data-testid="po-export-start-time" /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-white" data-testid="po-export-end-date" /></div>
            <div><Label>End Time</Label><Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="bg-white" data-testid="po-export-end-time" /></div>
          </div>
          {filterType === "supplier" && (
            <div><Label>Supplier</Label>
              <Select value={supplierId || undefined} onValueChange={setSupplierId}>
                <SelectTrigger className="bg-white" data-testid="po-export-supplier"><SelectValue placeholder="Semua Supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {filterType === "product" && (
            <div><Label>Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="bg-white" data-testid="po-export-product"><SelectValue placeholder="All Product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Product</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="po-export-excel-btn"><FileDown className="w-4 h-4 mr-1.5" />{busy ? "Menyiapkan..." : "Excel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PODetail({ po, onClose, onTransition, onExport, canOperate }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);

  const loadReceipts = async () => {
    setReceiptsLoading(true);
    try {
      const response = await api.get(`/purchase-orders/${po.id}/receipts`);
      setReceipts(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setReceiptsLoading(false);
    }
  };

  const selectTab = (tab) => {
    setActiveTab(tab);
    if (tab === "receipts") {
      loadReceipts();
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        aria-describedby="po-detail-description"
        className="max-w-4xl max-h-[88vh] overflow-y-auto"
        data-testid="po-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa flex items-center justify-between gap-3">
            <span>{po.number}{po.purchase_request_number ? ` · dari ${po.purchase_request_number}` : ""}</span>
            <Button size="sm" variant="outline" onClick={() => onExport(po)} className="border-status-passed text-status-passed h-7 px-2 mr-6" data-testid="po-detail-export-btn"><FileDown className="w-3.5 h-3.5 mr-1" />Export</Button>
          </DialogTitle>
          <DialogDescription className="sr-only" id="po-detail-description">
            Rincian Purchase Order dan riwayat penerimaan barang.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
          <div><div className="text-xs text-wmuted">Supplier</div><div>{po.supplier_name || "-"}</div></div>
          <div><div className="text-xs text-wmuted">Tanggal PO</div><div>{fmtDate(po.po_date)}</div></div>
          <div><div className="text-xs text-wmuted">Estimasi Datang</div><div>{po.est_arrival_date ? fmtDate(po.est_arrival_date) : "-"}</div></div>
          <div><div className="text-xs text-wmuted">Status</div><div><StatusBadge status={po.status} /></div></div>
          {po.source === "production" && (
            <div>
              <div className="text-xs text-wmuted">Referensi Produksi</div>
              <button
                className="font-mono text-sm font-semibold text-cocoa hover:underline"
                data-testid="po-detail-production-reference"
                onClick={() => navigate(`/production/orders?detail=${po.production_order_id}`)}
                type="button"
              >
                {po.production_order_number || "Tidak ditemukan"}
              </button>
            </div>
          )}
        </div>
        <div className="mb-4 flex gap-2 border-b border-taupe/20" data-testid="po-detail-tabs">
          <button
            className={`border-b-2 px-1 py-2 text-sm font-medium ${activeTab === "overview" ? "border-cocoa text-cocoa" : "border-transparent text-wmuted hover:text-cocoa"}`}
            data-testid="po-detail-overview-tab"
            onClick={() => selectTab("overview")}
            type="button"
          >
            Detail PO
          </button>
          <button
            className={`border-b-2 px-1 py-2 text-sm font-medium ${activeTab === "receipts" ? "border-cocoa text-cocoa" : "border-transparent text-wmuted hover:text-cocoa"}`}
            data-testid="po-detail-receipts-tab"
            onClick={() => selectTab("receipts")}
            type="button"
          >
            Riwayat Penerimaan
          </button>
        </div>
        {activeTab === "overview" ? <>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Produk</TableHead><TableHead>Varian</TableHead>
              <TableHead className="text-right">Ordered</TableHead><TableHead className="text-right">Shipped</TableHead>
              <TableHead className="text-right">Received</TableHead><TableHead className="text-right">Remaining</TableHead>
              <TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Subtotal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {po.items.map((it, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium text-cocoa">{it.product_name}</TableCell>
                <TableCell className="text-sm">{it.color}/{it.size}</TableCell>
                <TableCell className="text-right">{it.qty}</TableCell>
                <TableCell className="text-right">{it.shipped_qty}</TableCell>
                <TableCell className="text-right">{it.received_qty}</TableCell>
                <TableCell className="text-right font-semibold">{it.remaining_qty}</TableCell>
                <TableCell className="text-right">{fmtIDR(it.unit_price)}</TableCell>
                <TableCell className="text-right">{fmtIDR(it.subtotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4 flex justify-end">
          <div className="text-sm space-y-1 text-right">
            <div>Subtotal: <span className="font-semibold">{fmtIDR(po.subtotal)}</span></div>
            <div>Diskon: <span className="font-semibold">{fmtIDR(po.discount)}</span></div>
            <div>Biaya tambahan: <span className="font-semibold">{fmtIDR(po.additional_cost)}</span></div>
            <div className="text-lg font-bold text-cocoa">Total: {fmtIDR(po.total)}</div>
          </div>
        </div>

        {po.created_by && <div className="mt-3 text-xs text-wmuted">Dibuat oleh {po.created_by}{po.approved_by ? ` · Disetujui oleh ${po.approved_by} pada ${fmtDate(po.approved_at)}` : ""}</div>}

        {OPS_NEXT[po.status] && canOperate && (
          <div className="mt-4 flex gap-2 items-center flex-wrap">
            <span className="text-sm text-wmuted">Ubah status:</span>
            {OPS_NEXT[po.status].map((s) => (
              <Button key={s} size="sm" onClick={() => onTransition(po.id, s)} data-testid={`po-transition-${s.replace(/ /g, "-")}`} className="bg-taupe hover:bg-taupe/90 text-white h-7 px-2">{s}</Button>
            ))}
          </div>
        )}

        {po.approval_history?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-cocoa mb-1">Riwayat Approval</h4>
            <div className="space-y-1" data-testid="po-approval-history">
              {po.approval_history.map((h, i) => (
                <div key={i} className="text-xs border-b border-taupe/10 py-1 flex justify-between">
                  <span className={h.decision === "approved" ? "text-status-passed" : "text-status-reject"}>
                    {h.decision === "approved" ? "✔ Disetujui" : "✘ Ditolak"} oleh {h.by_name}{h.reason ? ` — ${h.reason}` : ""}
                  </span>
                  <span className="text-wmuted">{fmtDate(h.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        </> : <ReceiptHistory loading={receiptsLoading} receipts={receipts} />}
      </DialogContent>
    </Dialog>
  );
}

function ReceiptHistory({ loading, receipts }) {
  if (loading) {
    return <div className="py-10 text-center text-wmuted" data-testid="po-receipts-loading">Memuat penerimaan...</div>;
  }
  if (!receipts.length) {
    return <div className="py-10 text-center text-wmuted" data-testid="po-receipts-empty">Belum ada penerimaan tercatat.</div>;
  }
  return (
    <div className="space-y-4" data-testid="po-receipts-history">
      {receipts.map((receipt) => (
        <div className="border border-taupe/20 p-3" data-testid={`po-receipt-${receipt.id}`} key={receipt.id}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-mono text-sm font-semibold text-cocoa">{receipt.receipt_number}</div>
              <div className="text-xs text-wmuted">{fmtDate(receipt.received_at)} · {receipt.warehouse_name || "-"}</div>
            </div>
            <div className="text-right text-xs text-wmuted">
              <div>{receipt.received_by || "-"}</div>
              <div className="font-semibold text-cocoa">{receipt.total_received || 0} pcs diterima</div>
            </div>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Produk / Varian</TableHead>
                <TableHead className="text-right">Diterima</TableHead>
                <TableHead className="text-right">Sisa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(receipt.items || []).map((item, index) => (
                <TableRow
                  data-testid={`po-receipt-item-${receipt.id}-${index}`}
                  key={`${receipt.id}-${item.variant_id || index}`}
                >
                  <TableCell>
                    <div className="font-medium text-cocoa">{item.product_name || "-"}</div>
                    <div className="text-xs text-wmuted">{item.color || "-"} / {item.size || "-"}</div>
                  </TableCell>
                  <TableCell className="text-right">{item.received_qty || 0}</TableCell>
                  <TableCell className="text-right">{item.remaining_qty || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          {receipt.notes && <div className="mt-2 text-xs text-wmuted">Catatan: {receipt.notes}</div>}
        </div>
      ))}
    </div>
  );
}
